/**
 * AppShell — the persistent chrome around every Admin screen.
 *
 * Sidebar (desktop) / Drawer + BottomNav (phone), the header with the branch
 * switcher and the account menu, the offline banner and the sync pill.
 *
 * The sidebar items come from `visibleNavEntries(...)`, i.e. from the SAME
 * registry the router reads. A screen can therefore never be in the menu but
 * unreachable, or reachable but invisible.
 */
import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  BottomNav,
  Drawer,
  IconButton,
  LoadingState,
  OfflineBanner,
  Sidebar,
  SyncStatusPill,
  useIsMobile,
  useLocalStorage,
  useOnlineStatus,
} from '@sonauto/ui';
import type { NavItem, NavLinkRenderProps } from '@sonauto/ui';
import { Suspense } from 'react';
import { GROUP_LABEL_VI, GROUP_ORDER, visibleNavEntries } from '../navigation.js';
import { useAuth } from '../auth/AuthProvider.js';
import { getLastSuccessAt } from '../lib/api.js';
import { AlertsBadge } from '../components/AlertsBadge.js';
import { NotificationsBell } from '../components/NotificationsBell.js';
import { BranchSwitcher } from './BranchSwitcher.js';

export function AppShell(): React.JSX.Element {
  const { has, me, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [dark, setDark] = useLocalStorage<boolean>('sonauto.admin.dark', false);

  // Considered offline if the API has not answered for 90 seconds, even when
  // the OS insists there is a network — see the hook's comment.
  const online = useOnlineStatus({ lastSuccessAt: getLastSuccessAt(), staleAfterMs: 90_000 });

  // Applied here rather than in main.tsx so the toggle takes effect instantly.
  if (typeof document !== 'undefined') {
    document.documentElement.classList.toggle('dark', dark);
  }

  const entries = visibleNavEntries(has);
  const items: NavItem[] = entries.map((entry) => ({
    id: entry.id,
    label: entry.titleVi,
    to: entry.path,
    icon: entry.icon,
    group: entry.group,
  }));

  const renderLink = ({ item, className, children }: NavLinkRenderProps): React.JSX.Element => (
    <NavLink to={item.to} end={item.to === '/'} className={className} onClick={() => setMobileNavOpen(false)}>
      {children}
    </NavLink>
  );

  const sidebarHeader = (
    <div className="flex items-center gap-2">
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-600 text-lg text-white">🍜</span>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-fg">SonAuto Admin</p>
        <p className="truncate text-xs text-fg-subtle">{me?.tenant.name ?? '—'}</p>
      </div>
    </div>
  );

  const sidebarFooter = (
    <div className="flex flex-col gap-2">
      <NavLink
        to="/profile"
        className="flex min-h-touch items-center gap-2 rounded-md px-2 text-sm text-fg-muted hover:bg-surface-alt hover:text-fg"
      >
        <span aria-hidden="true">👤</span>
        <span className="min-w-0 flex-1 truncate">{me?.user.fullName ?? me?.user.username ?? '—'}</span>
      </NavLink>
      <button
        type="button"
        onClick={() => {
          void logout().then(() => navigate('/', { replace: true }));
        }}
        className="flex min-h-touch items-center gap-2 rounded-md px-2 text-sm text-danger-fg hover:bg-danger-bg"
      >
        <span aria-hidden="true">⏻</span>
        Đăng xuất
      </button>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-canvas">
      <OfflineBanner online={online} />

      <div className="flex min-h-0 flex-1">
        {!isMobile ? (
          <Sidebar
            items={items}
            groupOrder={GROUP_ORDER}
            groupLabels={GROUP_LABEL_VI}
            activePath={location.pathname}
            renderLink={renderLink}
            header={sidebarHeader}
            footer={sidebarFooter}
            className="hidden sm:flex"
          />
        ) : null}

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex items-center gap-2 border-b border-line bg-surface px-3 py-2 sm:px-4">
            {isMobile ? (
              <IconButton label="Mở menu" icon={<span aria-hidden="true">☰</span>} onClick={() => setMobileNavOpen(true)} />
            ) : null}

            <BranchSwitcher />

            <div className="ml-auto flex items-center gap-2">
              <SyncStatusPill
                state={online ? 'synced' : 'offline'}
                lastSyncedAt={getLastSuccessAt()}
                className="hidden sm:inline-flex"
              />
              {/* Both render null when the user lacks the matching permission. */}
              <AlertsBadge />
              <NotificationsBell />
              <IconButton
                label={dark ? 'Chuyển sang giao diện sáng' : 'Chuyển sang giao diện tối'}
                icon={<span aria-hidden="true">{dark ? '☀️' : '🌙'}</span>}
                onClick={() => setDark(!dark)}
              />
            </div>
          </header>

          <main className="min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-6 sm:py-6">
            {/* Route components are lazy; this is the boundary that shows while
                a screen's chunk downloads. */}
            <Suspense fallback={<LoadingState label="Đang mở màn hình…" />}>
              <Outlet />
            </Suspense>
          </main>

          {isMobile ? (
            <BottomNav items={items.slice(0, 5)} activePath={location.pathname} renderLink={renderLink} />
          ) : null}
        </div>
      </div>

      <Drawer
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        title="Điều hướng"
        side="left"
        sizeClassName="w-72"
      >
        <Sidebar
          items={items}
          groupOrder={GROUP_ORDER}
          groupLabels={GROUP_LABEL_VI}
          activePath={location.pathname}
          renderLink={renderLink}
          footer={sidebarFooter}
          className="w-full border-0"
        />
      </Drawer>
    </div>
  );
}
